import { parsePositiveInteger } from 'src/shared/urlValidators';
import React from 'react';
import Helmet from 'react-helmet';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { FullScreenImageViewer, ViewerImage } from 'src/client/components/FullScreenImageViewer/FullScreenImageViewer';
import { StyledButton } from 'src/client/components/ui/Button';
import { useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { UNAUTHORIZED_ROUTE } from 'src/client/constants/joinCampaigns';
import NotFound from 'src/client/containers/NotFound/NotFound';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { useSfwViewerImages } from 'src/client/requests/useSfwImages';
import { canViewSfwImages, getSfwPageNumberForImage, getSfwTotalImages } from 'src/shared/sfwImages';
import { getSfwImagePath, getSfwPagePath } from './paths';

type ViewerProps = { number: number; totalImages: number };

const SfwImageViewer = (props: ViewerProps) => {
  const { number, totalImages } = props;
  const history = useHistory();
  const { data, isError, refetch } = useSfwViewerImages(number);
  const signedImage = (imageNumber: number): ViewerImage | undefined => {
    const image = data?.images.find(({ number: signedNumber }) => signedNumber === imageNumber);
    return image ? { alt: `SFW image ${imageNumber}`, src: image.url } : undefined;
  };
  const backPath = getSfwPagePath(getSfwPageNumberForImage(number));

  if (isError) {
    return (
      <main>
        <p>Unable to load SFW image {number}.</p>
        <StyledButton onClick={() => refetch()}>Try again</StyledButton>
        <StyledButton onClick={() => history.push(backPath)}>Back to SFW Images</StyledButton>
      </main>
    );
  }

  return (
    <FullScreenImageViewer
      currentImage={signedImage(number)}
      previousImage={signedImage(number - 1)}
      nextImage={signedImage(number + 1)}
      position={number}
      total={totalImages}
      autoSize
      resetKey={String(number)}
      onNavigate={(nextNumber) => history.push(getSfwImagePath(nextNumber))}
      onClose={() => history.push(backPath)}
    />
  );
};

export const SfwImageViewerRoute = () => {
  const { loaded, user } = useSelector((state) => state.auth);
  const config = useSelector((state) => state.app.config);
  const { number: rawNumber } = useParams<{ number: string }>();
  const [joinUrl] = useJoinUrl({ campaign: UNAUTHORIZED_ROUTE });

  if (!loaded) return null;
  const number = parsePositiveInteger(rawNumber);
  const totalImages = getSfwTotalImages(config);
  if (number === null || number > totalImages) return <NotFound />;
  if (!user) return <Redirect to={joinUrl} />;
  if (!canViewSfwImages(user)) return <NotFound />;

  return (
    <>
      <Helmet>
        <title>{`SFW Image ${number}`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <SfwImageViewer number={number} totalImages={totalImages} />
    </>
  );
};
